import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import BrandMark from '@/components/BrandMark'
import { publicOrigin } from '@/lib/platform'

type Mode = 'password' | 'magic' | 'reset'

// Decode a known deep-link path into a friendly explanation. Saved by App.tsx
// when an unauthenticated user lands somewhere that needs auth (e.g. a QR
// scan into a New Issue form).
function describeReturnTo(path: string | null): string | null {
  if (!path) return null
  if (path.includes('/projects/') && path.endsWith('/qr')) return 'print QR labels for a project'
  if (path.includes('/projects/') && path.includes('/new')) {
    try {
      const loc = new URL(path, 'https://x').searchParams.get('location')
      if (loc) return `log a new issue at "${decodeURIComponent(loc)}"`
    } catch {}
    return 'log a new issue'
  }
  if (path.startsWith('/projects/')) return 'open a project'
  if (path.startsWith('/issues/')) return 'open an issue'
  if (path.startsWith('/diary')) return 'open the diary'
  return null
}

export default function Login() {
  const { signIn, signInMagic } = useAuth()
  const [mode, setMode] = useState<Mode>('password')
  const returnTo = typeof window !== 'undefined' ? localStorage.getItem('ss.return_to') : null
  const returnHint = describeReturnTo(returnTo)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [magicSent, setMagicSent] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setBusy(true)
    try {
      if (mode === 'password') {
        const r = await signIn(email, password)
        if (r.error) setError(r.error)
      } else if (mode === 'magic') {
        const r = await signInMagic(email)
        if (r.error) setError(r.error)
        else setMagicSent(true)
      } else {
        // mode === 'reset'
        const redirectTo = publicOrigin() + '/'
        const { error: rErr } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo })
        if (rErr) setError(rErr.message)
        else setResetSent(true)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <header className="app-bar"><h1>Signed and Sorted</h1></header>
      <main>
        <div className="welcome">
          <BrandMark size={72} />
          <h2 style={{ marginTop: 'var(--space-3)' }}>{mode === 'reset' ? 'Reset password' : 'Sign in'}</h2>
          <p>{mode === 'reset' ? 'We\'ll email you a link to set a new one.' : 'Issue tracking that works on your phone.'}</p>
        </div>

        {returnHint && (
          <div className="card" style={{ background: 'var(--accent-pale)', borderColor: 'var(--accent)', padding: 'var(--space-2) var(--space-3)', marginBottom: 'var(--space-3)' }}>
            <p style={{ margin: 0, fontSize: 13 }}>
              📱 <strong>You're trying to {returnHint}.</strong> Sign in below to continue — we'll take you straight there.
            </p>
          </div>
        )}

        {magicSent ? (
          <div className="card">
            <h3>Check your email</h3>
            <p>We sent a sign-in link to <strong>{email}</strong>. Tap it to come back to the app.</p>
          </div>
        ) : resetSent ? (
          <div className="card">
            <h3>Check your email</h3>
            <p>If an account exists for <strong>{email}</strong>, we sent a password-reset link. Tap it to set a new password.</p>
            <button className="btn btn-ghost" style={{ marginTop: 'var(--space-3)' }}
              onClick={() => { setResetSent(false); setMode('password') }}>Back to sign in</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label>Email</label>
              <input type="email" required autoComplete="email" autoFocus
                value={email} onChange={e => { setEmail(e.target.value); setError(null) }}
                onFocus={() => {
                  // Fire-and-forget warm-up. By the time the user finishes
                  // typing their email + password, Supabase Auth is awake.
                  // Saves the cold-start wait on Sign In.
                  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/keep-warm`, { keepalive: true, cache: 'no-store' }).catch(() => {})
                }}
                placeholder="you@example.com" />
            </div>
            {mode === 'password' && (
              <div className="field">
                <label>Password</label>
                <input type="password" required autoComplete="current-password"
                  value={password} onChange={e => { setPassword(e.target.value); setError(null) }} />
              </div>
            )}
            {error && <p className="help" style={{ color: 'var(--danger)' }}>{error}</p>}
            <button className="btn btn-primary" type="submit" disabled={busy || !email || (mode === 'password' && !password)}>
              {busy ? 'Working…'
                : mode === 'password' ? 'Sign in'
                : mode === 'magic' ? 'Email me a sign-in link'
                : 'Email me a reset link'}
            </button>

            {mode === 'password' && (
              <button type="button" className="link-btn" style={{ marginTop: 'var(--space-2)' }}
                onClick={() => { setMode('reset'); setError(null) }}>
                Forgot password?
              </button>
            )}

            <button type="button" className="btn btn-ghost" style={{ marginTop: 'var(--space-2)' }}
              onClick={() => {
                setMode(m => m === 'password' ? 'magic' : 'password')
                setError(null)
              }}>
              {mode === 'reset' ? 'Back to sign in' : mode === 'password' ? 'Use a sign-in link instead' : 'Use a password instead'}
            </button>
          </form>
        )}

        <p className="muted" style={{ textAlign: 'center', marginTop: 'var(--space-5)' }}>
          No account? <Link to="/signup" style={{ color: 'var(--accent)', fontWeight: 600 }}>Start free</Link>
        </p>
      </main>
    </>
  )
}
